import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';

export interface SubscriptionPlan {
  id: string;
  planId: string;          // e.g. 'licenced_venue'
  name: string;
  description: string;
  priceMonthly: number;    // in cents, e.g. 4900
  priceUpfront?: number;   // in cents, e.g. 53900 — present if upfront option exists
  priceYearly?: number;    // legacy field, kept for compatibility
  currency: string;
  isLicenced?: boolean;    // true for licenced venue plans
  limits: {
    maxVenues: number;
    offersPerVenuePerMonth: number;
    totalOffersPerMonth: number;
    analyticsRetentionMonths: number;
    supportLevel: string;
  };
  features: {
    multiVenueManagement: boolean;
    advancedAnalytics: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    prioritySupport: boolean;
    whiteLabel?: boolean;
    dedicatedSupport?: boolean;
  };
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
  contactSales?: boolean;
}

export type BillingType = 'monthly' | 'upfront';

export const useSubscriptionPlans = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔄 Fetching subscription plans from Firestore...');

      const plansQuery = query(
        collection(db, 'admin_subscription_plans'),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc')
      );

      const plansSnapshot = await getDocs(plansQuery);
      const allPlans = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SubscriptionPlan));

      // Show only the two live plans (sortOrder 1 = non-licenced, 2 = licenced)
      const filteredPlans = allPlans.filter(plan =>
        plan.sortOrder === 1 || plan.sortOrder === 2
      );

      console.log(`✅ Loaded ${filteredPlans.length} subscription plans:`,
        filteredPlans.map(p => `${p.name} (order: ${p.sortOrder})`));

      setPlans(filteredPlans);
    } catch (err: any) {
      console.error('❌ Error fetching subscription plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return { plans, loading, error, refetch: fetchPlans };
};